import React, { useState } from "react";
import { Button, Pagination, ConfirmationModal } from "../../shared";
import { useGetPrices } from "../../hooks/price";
import { UpdatePricesModal } from "../../features";
import PricesTable from "./components/PricesTable";
import "./PricesPage.scss";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axiosConfig";

type PricesPageProps = {};

const PricesPage: React.FC<PricesPageProps> = () => {
  const [page, setPage] = useState(1);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [recordToRestore, setRecordToRestore] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState<{
    progress: number;
    status: string;
    message: string;
  } | null>(null);
  const [eventSourceRef, setEventSourceRef] = useState<EventSource | null>(
    null
  );
  const { data, loading, refetch } = useGetPrices(page);
  const navigate = useNavigate();

  const handleUpdateSuccess = () => {
    // Refresh the data after successful update
    refetch();
  };

  const handleExpandRecord = (recordId: string) => {
    navigate(`/dashboard/prices/${recordId}`);
  };

  const handleRestoreClick = (recordId: string) => {
    setRecordToRestore(recordId);
    setIsRestoreModalOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!recordToRestore) return;

    let eventSource: EventSource | null = null;

    try {
      setIsRestoring(true);
      setRestoreProgress({
        progress: 0,
        status: "Rozpoczynanie",
        message: "Rozpoczynanie przywracania cen...",
      });

      // Call restore endpoint which returns taskId immediately
      const response = await axiosInstance.post(
        `/allegro/sync/prices/restore?priceId=${recordToRestore}`
      );

      const taskId = response.data.taskId;

      if (!taskId) {
        throw new Error("No task ID received");
      }

      // Set up Server-Sent Events to track progress
      const baseURL = axiosInstance.defaults.baseURL || "";
      const streamUrl = `${baseURL}/prices/progress/${taskId}/stream`;

      eventSource = new EventSource(streamUrl, { withCredentials: true });
      setEventSourceRef(eventSource);

      // Wait for processing to complete
      await new Promise<void>((resolve, reject) => {
        eventSource!.onmessage = (event) => {
          const progressData = JSON.parse(event.data);
          setRestoreProgress(progressData);
          // Close connection when complete
          if (progressData.progress >= 100) {
            if (
              progressData.status === "completed" ||
              progressData.status === "Ukończono"
            ) {
              resolve();
            } else if (progressData.status === "error") {
              reject(new Error(progressData.message));
            }
          }
        };

        eventSource!.onerror = (error) => {
          reject(new Error("Błąd połączenia podczas przywracania cen"));
        };
      });

      eventSource?.close();
      setEventSourceRef(null);

      // Refresh the data after successful restore
      await refetch();

      // Small delay to ensure UI updates
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Close modal
      setIsRestoreModalOpen(false);
      setRecordToRestore(null);
      setRestoreProgress(null);
    } catch (error: any) {
      console.error("Error restoring prices:", error);
      eventSource?.close();
      setEventSourceRef(null);
      // You might want to show an error toast here
    } finally {
      setIsRestoring(false);
    }
  };

  const handleRestoreCancel = () => {
    // Prevent closing if restoration is in progress
    if (isRestoring) {
      return;
    }

    // Clean up event source if still open
    if (eventSourceRef) {
      eventSourceRef.close();
      setEventSourceRef(null);
    }

    setIsRestoreModalOpen(false);
    setRecordToRestore(null);
    setRestoreProgress(null);
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <>
      <div className="prices-page">
        <div className="prices-page__header">
          <h1>Aktualizacja cen</h1>
          <Button
            label="Aktualizuj ceny"
            onClick={() => setIsUpdateModalOpen(true)}
            styleType="primary"
          />
        </div>

        <PricesTable
          data={data?.data || []}
          showExpandedView={false}
          onExpandRecord={handleExpandRecord}
          onRestore={handleRestoreClick}
        />

        {data?.meta && data?.meta?.lastPage > 1 && (
          <Pagination
            totalPages={data?.meta?.lastPage}
            currentPage={data?.meta?.currentPage || page}
            onPageChange={(newPage: number) => setPage(newPage)}
          />
        )}
      </div>

      <UpdatePricesModal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        onSuccess={handleUpdateSuccess}
      />

      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        onClose={handleRestoreCancel}
        onConfirm={handleRestoreConfirm}
        title="Przywracanie cen"
        message={
          <>
            {!isRestoring ? (
              <p>
                Czy na pewno chcesz przywrócić ceny z tej aktualizacji?
                <br />
                <strong>
                  Spowoduje to cofnięcie zmian cen na platformie Allegro.
                </strong>
              </p>
            ) : restoreProgress ? (
              <div className="restore-progress">
                <div className="restore-progress__bar-container">
                  <div
                    className="restore-progress__bar"
                    style={{ width: `${restoreProgress.progress}%` }}
                  />
                </div>
                <div className="restore-progress__info">
                  <span className="restore-progress__percentage">
                    {Math.round(restoreProgress.progress)}%
                  </span>
                  <span className="restore-progress__message">
                    {restoreProgress.message}
                  </span>
                </div>
              </div>
            ) : (
              <p>Przywracanie...</p>
            )}
          </>
        }
        confirmLabel={
          isRestoring
            ? restoreProgress
              ? `${Math.round(restoreProgress.progress)}% - ${
                  restoreProgress.status
                }`
              : "Przywracanie..."
            : "Przywróć"
        }
        cancelLabel="Anuluj"
        confirmDisabled={isRestoring}
        cancelDisabled={isRestoring}
      />
    </>
  );
};

export default PricesPage;
